import type { Control } from "react-hook-form";

import { Controller } from "react-hook-form";

import { FieldError, Input, Label, TextField } from "@/components/field";
import {
  Select,
  SelectButton,
  SelectListBox,
  SelectListItem,
  SelectPopover,
} from "@/components/select";
import { ControlledTipTapEditor } from "@/components/ui/controlled/ControlledTipTapEditor";

import type { BrandKnowledgeNoteFormValues } from "../schema";

import { noteTypeOptions } from "../constants";

interface NewNoteFormFieldsProps {
  readonly control: Control<BrandKnowledgeNoteFormValues>;
  readonly editorKey?: number;
  readonly errors: Partial<
    Record<keyof BrandKnowledgeNoteFormValues, { message?: string }>
  >;
}

export function NewNoteFormFields({
  control,
  editorKey,
  errors,
}: NewNoteFormFieldsProps) {
  return (
    <div className="flex flex-col gap-lg">
      <Controller
        control={control}
        name="name"
        render={({ field }) => (
          <TextField isInvalid={!!errors.name} isRequired>
            <Label requiredHint>Name</Label>
            <Input {...field} placeholder="Add a short note name to the note" />
            <FieldError>{errors.name?.message}</FieldError>
          </TextField>
        )}
      />

      <Controller
        control={control}
        name="noteType"
        render={({ field }) => (
          <Select
            isInvalid={!!errors.noteType}
            isRequired
            onSelectionChange={field.onChange}
            placeholder="Choose how this note should guide design decisions."
            selectedKey={field.value}
          >
            <Label requiredHint>Note type</Label>
            <SelectButton />
            <SelectPopover>
              <SelectListBox items={noteTypeOptions}>
                {(item) => (
                  <SelectListItem
                    className="cursor-pointer"
                    id={item.value}
                    textValue={item.label}
                  >
                    {item.label}
                  </SelectListItem>
                )}
              </SelectListBox>
            </SelectPopover>
            <FieldError>{errors.noteType?.message}</FieldError>
          </Select>
        )}
      />

      <div className="flex flex-col gap-1.5 mb-6">
        <Label>Description</Label>
        <ControlledTipTapEditor
          className="h-[240px] max-w-full @container"
          control={control}
          key={editorKey}
          name="description"
          placeholder="Enter your brand note to the design team"
        />
        {errors.description && (
          <p className="text-xs text-text-error">
            {errors.description.message}
          </p>
        )}
      </div>
    </div>
  );
}
